import { AxiosRequestConfig } from 'axios'

import { Endpoint } from 'constants/API'
import { useAuth0 } from 'utils/auth'

import API from './API'
import { useAPI } from './useApi'

/**
 * `useAPI` for the v2 data model: every request asks for the caller's v2 role mimetype, and a request with a
 * body also sends it as its Content-Type. The endpoints tell v1 from v2 by mimetype alone, so a v2 call made
 * through `useAPI` gets the v1 model or a 406.
 *
 * @example
 * const api = useAPIV2<Endpoints>()
 * api.post<SessionPricingV2DTO>(Endpoints.SESSION_PRICING, { workspaceId, sessionId }, body, false)
 */
export const useAPIV2 = <EndpointType extends string = Endpoint>() => {
  const api = useAPI<EndpointType>()
  const { roles } = useAuth0()
  const mimetype = API.decideRoleMimeTypeV2(roles)

  const read = (options?: AxiosRequestConfig): AxiosRequestConfig => ({ ...options, headers: { Accept: mimetype } })
  const write = (options?: AxiosRequestConfig): AxiosRequestConfig => ({ ...options, headers: { Accept: mimetype, 'Content-Type': mimetype } })

  const get = ((endpoint, replacements, keepResponse, options, errorReporting) =>
    api.get(endpoint, replacements, keepResponse as false, read(options), errorReporting)) as typeof api.get
  const post = ((endpoint, replacements, body, keepResponse, options) =>
    api.post(endpoint, replacements, body, keepResponse as false, write(options))) as typeof api.post
  const put = ((endpoint, replacements, body, keepResponse, options) =>
    api.put(endpoint, replacements, body, keepResponse as false, write(options))) as typeof api.put

  return { get, post, put }
}
